import React from 'react';
import {
  Card,
  useClickableCard,
  StackLayout,
  StackItem,
  Button,
  useThemeStrings,
} from '@leagueplatform/genesis-core';
import type { MasonryEngineNode } from '@leagueplatform/masonry-engine';
import { type WidgetLink, WidgetType } from '@leagueplatform/masonry-api';
import { NotificationBannerContent } from './notification-banner-content';
import { useDismissibleAction } from './use-dismiss-action-hook';

export interface NotificationBannerWidgetProps {
  body: string;
  dismissibleLink?: WidgetLink;
  heading: string;
  image?: string;
  imageAltText?: string;
  imageLoading?: 'lazy' | 'eager';
  onCardClick?: (e: React.MouseEvent) => void;
  onDismiss?: () => void;
  url?: string;
}

export type NotificationBannerWSNode = MasonryEngineNode<
  WidgetType.NOTIFICATION_BANNER,
  NotificationBannerWidgetProps
>;

export const NotificationBannerWidget = ({
  body,
  dismissibleLink,
  heading,
  image,
  imageAltText,
  imageLoading = 'lazy',
  onCardClick,
  onDismiss: onDismissAnalytics,
  url,
}: NotificationBannerWidgetProps) => {
  const strings = useThemeStrings();

  const isClickable = !!(url || onCardClick);
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard();

  const { dismissed, onDismiss } = useDismissibleAction(
    dismissibleLink,
    onDismissAnalytics,
  );

  if (dismissed) return null;

  return (
    <Card.Elevated>
      <StackLayout orientation="horizontal" verticalAlignment="stretch">
        {isClickable ? (
          <Card.ClickArea
            css={{
              display: 'flex',
              flexDirection: 'row',
              gap: '$one',
              padding: '$threeQuarters',
              paddingInlineEnd: '$half',
            }}
            ref={clickAreaRef}
            onCardMouseUp={onCardMouseUp}
            onCardMouseDown={onCardMouseDown}
          >
            <NotificationBannerContent
              body={body}
              heading={heading}
              image={image}
              imageLoading={imageLoading}
              imageAltText={imageAltText}
              onCardClick={onCardClick}
              ref={primaryActionRef}
              url={url}
            />
          </Card.ClickArea>
        ) : (
          <StackLayout
            orientation="horizontal"
            spacing="$one"
            verticalAlignment="center"
            css={{
              padding: '$threeQuarters',
              paddingInlineEnd: '$half',
            }}
          >
            <NotificationBannerContent
              body={body}
              heading={heading}
              image={image}
              imageLoading={imageLoading}
              imageAltText={imageAltText}
            />
          </StackLayout>
        )}

        {dismissibleLink && (
          <StackItem
            verticalAlignment="top"
            css={{ padding: '$threeQuarters', paddingInlineStart: '$half' }}
          >
            <Button
              hideLabel
              icon="tinyClose"
              onClick={onDismiss}
              priority="tertiary"
              quiet
              size="small"
            >
              {strings.dismiss}
            </Button>
          </StackItem>
        )}
      </StackLayout>
    </Card.Elevated>
  );
};
